import { useCallback, useEffect, useState } from "react";
import { VerifiedBadge } from "@/components/market/AndroidVerification";
import { vLabel } from "@/lib/version";
import { toast } from "sonner";
import { ChevronDown, Pause, Play, Sparkles, Trash2, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { GraceBadge } from "@/components/admin/GraceCard";
import { ConfirmButton } from "@/components/admin/ConfirmButton";
import { StatusBadge } from "@/components/dev/BillingCard";
import { ProfitDot, useProfit } from "@/components/admin/Profit";
import { OfferForm as CustomPlanForm } from "@/components/offers/OfferForm";
import { TrialTools } from "@/components/admin/TrialTools";
import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";

const DevApps = ({ devId, onChanged }) => {
  const { t, fmtBytes, fmtDownloads } = useI18n();
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => api.get(`/admin/developers/${devId}`).then(({ data }) => setDetail(data)), [devId]);
  useEffect(() => {
    load();
  }, [load]);
  const removeApp = async (id) => {
    try {
      await api.delete(`/admin/developers/apps/${id}`);
      toast.success(t("admin.appDeleted"));
      load();
      onChanged();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  if (!detail) return null;
  if (!detail.apps.length) return <p className="mt-3 text-xs text-neutral-500">{t("adminDev.noApps")}</p>;
  return (
    <ul className="mt-3 space-y-2" data-testid={`admin-dev-apps-${devId}`}>
      {detail.apps.map((a) => (
        <li key={a.id} className="flex items-center gap-2 rounded-xl border border-neutral-800 bg-black/40 p-3" data-testid={`admin-dev-app-${a.id}`}>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{a.name} <span className="text-xs font-normal text-[var(--gold-bright)]" data-testid={`admin-dev-app-downloads-${a.id}`}>· {fmtDownloads(a.downloads)}</span></p>
            {a.brand_flag && <p className="mt-1 break-words text-xs font-semibold text-orange-300" data-testid={`admin-dev-app-brand-flag-${a.id}`}>{t("brand.flag")} ({a.brand_flag})</p>}
            <p className="truncate text-xs text-neutral-500">{a.versions.map((v) => `${vLabel(v)} (${fmtBytes(v.size)}, ${fmtDownloads(v.downloads)}${v.hidden ? ", " + t("versions.hidden") : ""})`).join(" · ") || t("apps.noVersion")}</p>
          </div>
          <ConfirmButton title={t("admin.deleteAppTitle")} description={a.name} onConfirm={() => removeApp(a.id)} className="rounded-full p-2 text-red-300 hover:bg-white/5" testId={`admin-dev-app-delete-${a.id}`}>
            <Trash2 className="h-4 w-4" />
          </ConfirmButton>
        </li>
      ))}
    </ul>
  );
};

const DevRow = ({ d, onChanged, profit }) => {
  const { t, fmtDate, fmtNum } = useI18n();
  const [open, setOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(t(msg));
      onChanged();
    } catch (err) {
      toast.error(translateError(err, t));
    }
  };
  return (
    <li className="rounded-2xl border border-[rgba(245,197,66,0.2)] bg-black/30 p-4" data-testid={`admin-dev-row-${d.id}`}>
      <div className="flex flex-col gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <ProfitDot c={profit} testId={`admin-dev-profit-${d.id}`} />
            <p className="font-semibold text-white" data-testid="admin-dev-brand">{d.brand}</p>
            {!(d.kind === "minisite" && d.billing.trial?.status === "trialing") && <StatusBadge status={d.billing.status} testId={`admin-dev-status-${d.id}`} />}
            {d.billing.status === "late" && <GraceBadge until={d.billing.grace_until} testId={`admin-dev-grace-${d.id}`} />}
            {d.kind === "minisite" && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.65rem] font-bold uppercase text-emerald-200" data-testid={`admin-dev-kind-${d.id}`}>{t("client.eyebrow")}</span>}
            <span className="rounded-full border border-[var(--gold)] px-2 py-0.5 text-[0.65rem] uppercase text-[var(--gold-bright)]" data-testid={`admin-dev-plan-${d.id}`}>{d.plan === "custom" ? t("plans.customName", { n: fmtNum(d.billing.max_apps) }) : d.plan}{d.vip ? " · VIP" : ""}</span>
            {d.android_verified && <VerifiedBadge testId={`admin-dev-verified-${d.id}`} />}
          </div>
          <TrialTools d={d} onChanged={onChanged} />
          <p className="mt-1 break-words text-xs text-neutral-400">{d.name} · <span className="[overflow-wrap:anywhere]">{d.email}</span></p>
          {d.kind !== "minisite" && <p className="mt-1 text-xs text-neutral-400">
            <span data-testid={`admin-dev-period-${d.id}`}>{t("adminDev.periodEnd")}: {d.billing.period_end ? fmtDate(d.billing.period_end) : "—"}</span>
            {" · "}<span data-testid={`admin-dev-apps-count-${d.id}`}>{t("adminDev.apps", { n: d.apps_count })}</span>
          </p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-gold btn-sm" onClick={() => setOpen((o) => !o)} data-testid={`admin-dev-toggle-apps-${d.id}`}>
            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} /> {t("admin.apps")}
          </button>
          <button type="button" className="btn-gold btn-sm" onClick={() => setPlanOpen((o) => !o)} aria-expanded={planOpen} data-testid={`admin-dev-custom-plan-btn-${d.id}`}>
            <Sparkles className="h-4 w-4" /> {t("custom.rowBtn")}
          </button>
          {d.android_verification?.status === "verified" && (
            <button type="button" className="btn-gold btn-sm" data-testid={`admin-dev-verify-btn-${d.id}`}
              onClick={() => act(() => api.put(`/admin/developers/${d.id}/android-verification`, { enabled: !d.android_verified }), "verify.saved")}>
              {t(d.android_verified ? "verify.unconfirm" : "verify.confirm")}
            </button>
          )}
          <Link to={`/admin/messages?developer=${d.id}`} className="btn-gold btn-sm" data-testid={`admin-dev-messages-${d.id}`}>
            <MessageSquare className="h-4 w-4" /> {t("msg.title")}
          </Link>
          <button type="button" className="btn-red btn-sm" data-testid={`admin-dev-suspend-btn-${d.id}`}
            onClick={() => act(() => api.put(`/admin/developers/${d.id}/suspension`, { enabled: !d.admin_suspended }), d.admin_suspended ? "adminDev.restored" : "adminDev.suspended")}>
            {d.admin_suspended ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />} {t(d.admin_suspended ? "adminDev.restore" : "adminDev.suspend")}
          </button>
          <ConfirmButton title={t("adminDev.deleteTitle")} description={t("adminDev.deleteDesc", { name: d.brand })} className="btn-red btn-sm"
            onConfirm={() => act(() => api.delete(`/admin/developers/${d.id}`), "adminDev.deleted")} testId={`admin-dev-delete-btn-${d.id}`}>
            <Trash2 className="h-4 w-4" />
          </ConfirmButton>
        </div>
      </div>
      {planOpen && <CustomPlanForm key={d.custom_offer?.id || "none"} d={d} onChanged={onChanged} />}
      {open && <DevApps devId={d.id} onChanged={onChanged} />}
    </li>
  );
};

export const DevelopersCard = () => {
  const { t } = useI18n();
  const [profit] = useProfit();
  const [devs, setDevs] = useState(null);
  const load = useCallback(() => api.get("/admin/developers").then(({ data }) => setDevs(data)).catch(() => setDevs([])), []);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <section className="card-carbon rounded-2xl p-6" data-testid="admin-developers-card">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white">
        <Users className="h-5 w-5 text-[var(--gold)]" /> {t("adminDev.title")} <span className="text-[var(--gold)]" data-testid="admin-developers-count">({devs?.length ?? 0})</span>
      </h2>
      {devs?.length === 0 && <p className="mt-4 text-sm text-neutral-400" data-testid="admin-developers-empty">{t("adminDev.empty")}</p>}
      <ul className="mt-4 space-y-3">{devs?.map((d) => <DevRow key={d.id} d={d} onChanged={load} profit={profit?.clients?.[d.id]} />)}</ul>
    </section>
  );
};
